import { useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceHover: "#1a2234",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  accentGlow: "rgba(0, 229, 160, 0.3)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  error: "#f87171",
  errorBg: "rgba(248, 113, 113, 0.08)",
};

const CHART_CHIPS = ["Bar", "Line", "Pie", "Scatter", "Heatmap", "Table"];

function LoadingDots() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "16px 0" }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: theme.accent,
            animation: `pulse 1.2s ease-in-out ${i * 0.15}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes pulse {
          0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const LABELS_INSTRUCTION =
  "Always show data labels directly on the chart elements (not just on hover). " +
  "For scatter plots, label each data point with its name or identifier directly on the chart. " +
  "For bar charts, display the value on or above each bar. " +
  "For line charts, annotate key data points with their values.";

export default function VisualizeModal({ selectedQueries, session, onClose, onSuccess }) {
  const [description, setDescription] = useState("");
  const [showLabels, setShowLabels] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChipClick = (chip) => {
    setDescription((prev) => {
      const trimmed = prev.trim();
      if (!trimmed) return chip;
      // Replace existing chip word if it's already the last word
      const words = trimmed.split(/\s+/);
      if (CHART_CHIPS.map((c) => c.toLowerCase()).includes(words[words.length - 1].toLowerCase())) {
        return [...words.slice(0, -1), chip].join(" ");
      }
      return trimmed + " " + chip;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/visualize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          queries: selectedQueries.map((q) => ({
            prompt: q.prompt,
            sql: q.sql,
            results: q.results,
          })),
          user_description: [description.trim(), showLabels ? LABELS_INSTRUCTION : ""]
            .filter(Boolean)
            .join(". ") || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          setError("Too many requests. Please wait a moment and try again.");
        } else {
          setError(data.detail || "Failed to generate visualization. Try again or adjust your description.");
        }
      } else {
        onSuccess(data);
      }
    } catch {
      setError("Could not connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 24,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 16,
          width: "100%",
          maxWidth: 560,
          boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          fontFamily: "'IBM Plex Mono', monospace",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, ${theme.accentDim}, transparent)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>📊</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: theme.text, letterSpacing: -0.5 }}>
              Generate Visualization
            </span>
          </div>
          {!loading && (
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: theme.textMuted,
                fontSize: 18,
                cursor: "pointer",
                lineHeight: 1,
                padding: 4,
              }}
            >
              ×
            </button>
          )}
        </div>

        <div style={{ padding: "20px 24px" }}>
          {/* Selected queries summary */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, color: theme.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 10, marginTop: 0, fontWeight: 600 }}>
              Selected {selectedQueries.length === 1 ? "Query" : "Queries"}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {selectedQueries.map((q, i) => (
                <div
                  key={q.id}
                  style={{
                    padding: "8px 12px",
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    fontSize: 13,
                    color: theme.text,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <span style={{ color: theme.accent, fontWeight: 700, minWidth: 16 }}>{i + 1}.</span>
                  <span style={{ lineHeight: 1.5 }}>{q.prompt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart type chips */}
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 11, color: theme.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, marginTop: 0, fontWeight: 600 }}>
              Quick Select
            </p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {CHART_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => handleChipClick(chip)}
                  disabled={loading}
                  style={{
                    background: theme.accentDim,
                    border: `1px solid ${theme.accent}33`,
                    borderRadius: 20,
                    padding: "5px 12px",
                    fontSize: 12,
                    color: theme.accent,
                    cursor: loading ? "not-allowed" : "pointer",
                    fontFamily: "'IBM Plex Mono', monospace",
                    letterSpacing: 0.3,
                    transition: "all 0.15s",
                    opacity: loading ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => { if (!loading) e.target.style.background = theme.accentGlow; }}
                  onMouseLeave={(e) => { if (!loading) e.target.style.background = theme.accentDim; }}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Show labels toggle */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 18,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.5 : 1,
            }}
          >
            <div
              onClick={() => !loading && setShowLabels((v) => !v)}
              style={{
                width: 18,
                height: 18,
                borderRadius: 4,
                border: `1.5px solid ${showLabels ? theme.accent : theme.border}`,
                background: showLabels ? theme.accentDim : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.15s",
              }}
            >
              {showLabels && (
                <span style={{ color: theme.accent, fontSize: 12, lineHeight: 1, fontWeight: 700 }}>✓</span>
              )}
            </div>
            <span
              onClick={() => !loading && setShowLabels((v) => !v)}
              style={{ fontSize: 13, color: theme.text, userSelect: "none" }}
            >
              Always show data labels{" "}
              <span style={{ color: theme.textMuted, fontSize: 12 }}>
                (display names/values on chart, not just on hover)
              </span>
            </span>
          </label>

          {/* Description textarea */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 11, color: theme.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, marginTop: 0, fontWeight: 600 }}>
              Description <span style={{ color: theme.textDim, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
            </p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              placeholder={"Describe what you'd like to see (optional) — e.g., 'bar chart comparing passing yards', 'scatter plot of age vs performance', 'pie chart of team distribution'"}
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                color: loading ? theme.textDim : theme.text,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 13,
                padding: "10px 12px",
                resize: "vertical",
                minHeight: 80,
                outline: "none",
                transition: "border-color 0.2s",
                lineHeight: 1.6,
              }}
              onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
              onBlur={(e) => { e.target.style.borderColor = theme.border; }}
            />
          </div>

          {/* Loading state */}
          {loading && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <LoadingDots />
              <p style={{ fontSize: 13, color: theme.textMuted, margin: 0 }}>
                Generating your visualization...
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                background: theme.errorBg,
                border: `1px solid ${theme.error}33`,
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 13,
                color: theme.error,
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              disabled={loading}
              style={{
                background: "none",
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: "10px 20px",
                color: theme.textMuted,
                fontSize: 13,
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: loading ? theme.accentDim : `linear-gradient(135deg, ${theme.accent}, #00c98a)`,
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                color: loading ? theme.accent : theme.bg,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: loading ? "not-allowed" : "pointer",
                letterSpacing: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 8,
                transition: "all 0.2s",
              }}
            >
              {loading ? "Generating..." : "Generate Visualization"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
